// ============================================================================
// FSA TEMPLATE VIEW MODAL — read-only (FSA-1)
// ============================================================================
// Layout follows the RecSuit Litigation Case Detail pattern:
//
//   Header: title + system badge + refresh + close
//   Two-pane:
//     Sidebar (220px / 56px collapsible)
//       - General Info
//       - One row per report (Balance Sheet, Profit & Loss, Cash Flow,
//         Notes, Policy, Auditor Report, Director Report, CARO)
//     Content (right pane)
//       - General Info     → metadata cards (TemplateGeneralInfo)
//       - FINANCIAL_STATEMENT → ReportPrintPreview (Particulars/Note/CY/PY)
//       - NOTES_BUNDLE     → NotesIndex → NoteDetailView (drilldown)
//       - NARRATIVE        → NarrativePreview (HTML body + placeholders)
//   Footer: read-only badge + Close
//
// FSA-1 ships read-only. The format-definition technical previews shown
// in the previous iteration have been removed from the user-facing view —
// they still drive FSA-2/FSA-3 data entry on the API side.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { useFsaTemplateHierarchy } from 'hooks/api/fsa/useFsaTemplates';
import type {
  FsTemplate,
  TemplateReportNode,
  TemplateScheduleNode,
} from 'api/types/fsa/template.types';
import TemplateGeneralInfo from 'components/modules/fsa/templates/parts/TemplateGeneralInfo';
import ReportPrintPreview from 'components/modules/fsa/templates/parts/ReportPrintPreview';
import NotesIndex from 'components/modules/fsa/templates/parts/NotesIndex';
import NoteDetailView from 'components/modules/fsa/templates/parts/NoteDetailView';
import NarrativePreview from 'components/modules/fsa/templates/parts/NarrativePreview';
import FsaTemplatePdfPreviewModal from 'components/modals/modules/fsa/FsaTemplatePdfPreviewModal';

// ============================================================================
// TYPES
// ============================================================================

export interface FsaTemplateViewModalProps {
  isOpen: boolean;
  template: FsTemplate | null;
  onClose: () => void;
}

type ActiveItemId = 'general' | string; // 'general' or a report id

// Report icon map — matches the report code seeded in 023_seed_fsa_template_sch3_div1.sql.
const REPORT_ICON: Record<string, keyof typeof IconSystem> = {
  BS:                'file',
  PL:                'chart',
  CF:                'dollarSign',
  NOTES:             'list',
  POLICY:            'file',
  AUDIT_REPORT:      'check',
  DIRECTORS_REPORT:  'user',
  CARO:              'lock',
};

// ============================================================================
// COMPONENT
// ============================================================================

const FsaTemplateViewModal: React.FC<FsaTemplateViewModalProps> = ({ isOpen, template, onClose }) => {
  const { data: hierarchy, loading, error, refetch } = useFsaTemplateHierarchy(
    isOpen && template ? template.id : null
  );

  // Refetch on open if a different template was just selected.
  useEffect(() => {
    if (isOpen && template) refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, template?.id]);

  // ---- Active sidebar item ----
  const [activeItemId, setActiveItemId] = useState<ActiveItemId>('general');
  const [drilledNoteScheduleId, setDrilledNoteScheduleId] = useState<string | null>(null);

  // Reset selection on open / close.
  useEffect(() => {
    if (isOpen) {
      setActiveItemId('general');
      setDrilledNoteScheduleId(null);
    }
  }, [isOpen, template?.id]);

  // ---- Sidebar collapse (auto-collapse on narrow viewports) ----
  const [viewportNarrow, setViewportNarrow] = useState(false);
  const [manualState, setManualState] = useState<boolean | null>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 800px)');
    const onChange = () => setViewportNarrow(mq.matches);
    onChange();
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const sidebarCollapsed = manualState != null ? manualState : viewportNarrow;
  useEffect(() => {
    if (!isOpen) setManualState(null);
  }, [isOpen]);

  const toggleSidebar = useCallback(() => {
    setManualState((prev) => (prev != null ? !prev : !viewportNarrow));
  }, [viewportNarrow]);

  // ---- Resolve active report (if any) ----
  const reports = hierarchy?.reports ?? [];
  const activeReport: TemplateReportNode | null = useMemo(() => {
    if (activeItemId === 'general') return null;
    return reports.find((r) => r.report.id === activeItemId) ?? null;
  }, [activeItemId, reports]);

  const drilledNote: TemplateScheduleNode | null = useMemo(() => {
    if (!activeReport || !drilledNoteScheduleId) return null;
    return activeReport.schedules.find((s) => s.schedule.id === drilledNoteScheduleId) ?? null;
  }, [activeReport, drilledNoteScheduleId]);

  // Map BS/P&L "open note" cross-references to a click-to-drill behaviour:
  // jump to the Notes bundle and drill into the matching schedule.
  const handleOpenLinkedNote = useCallback(
    (linkedScheduleCode: string) => {
      const notesReport = reports.find((r) => r.report.reportType === 'NOTES_BUNDLE');
      if (!notesReport) return;
      const target = notesReport.schedules.find((s) => s.schedule.code === linkedScheduleCode);
      if (!target) return;
      setActiveItemId(notesReport.report.id);
      setDrilledNoteScheduleId(target.schedule.id);
    },
    [reports]
  );

  // ---- Preview & Print: open the server-rendered PDF in a sub-modal ----
  // Server-side QuestPDF (FsaTemplatePdfService) generates a binary PDF
  // that the sub-modal renders in an <iframe>. Replaces the previous
  // HTML-via-new-tab flow because Chrome's "Save as PDF" produced
  // unopenable output on the large Schedule III template.
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const handlePrintPreview = useCallback(() => {
    if (!template?.id) return;
    setPdfPreviewOpen(true);
  }, [template?.id]);

  // ---- Counts for General Info ----
  const counts = useMemo(() => {
    let scheduleCount = 0;
    let noteCount = 0;
    for (const r of reports) {
      scheduleCount += r.schedules.length;
      if (r.report.reportType === 'NOTES_BUNDLE') noteCount += r.schedules.length;
    }
    return { reportCount: reports.length, scheduleCount, noteCount };
  }, [reports]);

  if (!isOpen || !template) return null;

  // ============================================================================
  // CONTENT PANE
  // ============================================================================

  let content: React.ReactNode;
  if (loading && !hierarchy) {
    content = (
      <div style={loadingStyle}>
        <div style={spinnerStyle} />
        <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>Loading template…</span>
      </div>
    );
  } else if (error) {
    content = (
      <div style={errorStyle}>
        <strong>Failed to load template.</strong>
        <div style={{ fontSize: '0.8125rem', marginTop: 4 }}>{error.message}</div>
        <button className="btn btn-secondary" onClick={() => refetch()} style={{ marginTop: 8 }}>
          Retry
        </button>
      </div>
    );
  } else if (activeItemId === 'general') {
    content = (
      <TemplateGeneralInfo
        template={template}
        reportCount={counts.reportCount}
        scheduleCount={counts.scheduleCount}
        noteCount={counts.noteCount}
      />
    );
  } else if (activeReport) {
    if (activeReport.report.reportType === 'FINANCIAL_STATEMENT') {
      content = (
        <ReportPrintPreview
          reportNode={activeReport}
          onOpenNote={handleOpenLinkedNote}
        />
      );
    } else if (activeReport.report.reportType === 'NOTES_BUNDLE') {
      if (drilledNote) {
        content = (
          <NoteDetailView
            scheduleNode={drilledNote}
            onBack={() => setDrilledNoteScheduleId(null)}
          />
        );
      } else {
        content = (
          <NotesIndex
            reportNode={activeReport}
            onOpenNote={(sn) => setDrilledNoteScheduleId(sn.schedule.id)}
          />
        );
      }
    } else if (activeReport.report.reportType === 'NARRATIVE') {
      content = <NarrativePreview reportNode={activeReport} />;
    }
  } else {
    content = (
      <div style={{ padding: '1rem', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
        Select an item from the sidebar.
      </div>
    );
  }

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <>
      <div className="modal-backdrop" />
      <div ref={modalRef} className="modal-container modal-xl" style={{ display: 'flex', flexDirection: 'column' }}>

        {/* ---- Header ---- */}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', flexWrap: 'wrap', minWidth: 0, flex: 1 }}>
            <IconSystem.chart size={18} />
            <h2 className="modal-title" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {template.code} · {template.name}
            </h2>
            {template.isSystem && (
              <span style={systemBadgeStyle}>System</span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', flexShrink: 0 }}>
            <button
              className="modal-header-btn"
              onClick={handlePrintPreview}
              disabled={loading || !hierarchy}
              title="Open the full template (cover, BS, P&L, CF, all Notes, narratives) in a new tab — print or save as PDF for CA review"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '0.25rem 0.55rem', fontSize: '0.7rem', fontWeight: 600 }}
            >
              <IconSystem.print size={14} />
              <span>Preview &amp; Print</span>
            </button>
            <button className="modal-header-btn" onClick={() => refetch()} title="Refresh">
              <IconSystem.refresh size={16} />
            </button>
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* ---- Two-pane: sidebar + content ---- */}
        <div style={{ display: 'flex', flex: 1, minHeight: 0, overflow: 'hidden' }}>

          {/* ---- Sidebar ---- */}
          <aside
            style={{
              width: sidebarCollapsed ? 56 : 220,
              flexShrink: 0,
              borderRight: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)',
              overflowY: 'auto',
              padding: '0.5rem 0',
              transition: 'width 0.2s ease',
            }}
          >
            {/* Toggle */}
            <div
              style={{
                display: 'flex',
                justifyContent: sidebarCollapsed ? 'center' : 'flex-end',
                padding: '0 0.4rem 0.4rem',
              }}
            >
              <button
                type="button"
                onClick={toggleSidebar}
                title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                style={toggleButtonStyle}
              >
                {sidebarCollapsed
                  ? <IconSystem.chevronRight size={14} />
                  : <IconSystem.chevronLeft size={14} />}
              </button>
            </div>

            {/* General Info */}
            <SidebarItem
              icon={<IconSystem.info size={16} />}
              label="General Info"
              active={activeItemId === 'general'}
              collapsed={sidebarCollapsed}
              onClick={() => {
                setActiveItemId('general');
                setDrilledNoteScheduleId(null);
              }}
            />

            {/* Section divider */}
            {!sidebarCollapsed && reports.length > 0 && (
              <div style={sidebarSectionLabelStyle}>Reports</div>
            )}
            {sidebarCollapsed && reports.length > 0 && (
              <div style={sidebarSectionDividerStyle} />
            )}

            {/* Reports */}
            {reports.map((r) => {
              const iconKey = REPORT_ICON[r.report.code] ?? 'file';
              const Icon = (IconSystem as any)[iconKey] ?? IconSystem.file;
              const itemCount =
                r.report.reportType === 'NOTES_BUNDLE' ? r.schedules.length : null;

              return (
                <SidebarItem
                  key={r.report.id}
                  icon={<Icon size={16} />}
                  label={r.report.name}
                  badge={itemCount != null ? String(itemCount) : null}
                  active={activeItemId === r.report.id}
                  collapsed={sidebarCollapsed}
                  onClick={() => {
                    setActiveItemId(r.report.id);
                    setDrilledNoteScheduleId(null);
                  }}
                />
              );
            })}
          </aside>

          {/* ---- Content ---- */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem', minWidth: 0 }}>
            {content}
          </div>
        </div>

        {/* ---- Footer ---- */}
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>
            Read-only · FSA Stage 1
          </span>
          <div style={{ flex: 1 }} />
          <button className="btn btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>

      {/* PDF preview sub-modal (server-rendered, opens on Preview & Print) */}
      <FsaTemplatePdfPreviewModal
        isOpen={pdfPreviewOpen}
        templateId={template?.id ?? null}
        templateCode={template?.code ?? null}
        templateName={template?.name ?? null}
        onClose={() => setPdfPreviewOpen(false)}
      />
    </>
  );
};

export default FsaTemplateViewModal;

// ============================================================================
// SIDEBAR ITEM
// ============================================================================

const SidebarItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  active: boolean;
  collapsed: boolean;
  badge?: string | null;
  onClick: () => void;
}> = ({ icon, label, active, collapsed, badge, onClick }) => {
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={label}
        style={{
          ...sidebarItemCollapsedStyle,
          color: active ? '#2563eb' : 'var(--text-secondary)',
          background: active ? 'var(--bg-primary)' : 'transparent',
          borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
        }}
      >
        {icon}
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...sidebarItemStyle,
        color: active ? '#2563eb' : 'var(--text-primary)',
        background: active ? 'var(--bg-primary)' : 'transparent',
        borderLeft: active ? '3px solid #2563eb' : '3px solid transparent',
        fontWeight: active ? 600 : 500,
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0, flex: 1 }}>
        <span style={{ flexShrink: 0, color: active ? '#2563eb' : 'var(--text-secondary)' }}>{icon}</span>
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      </span>
      {badge != null && (
        <span style={sidebarBadgeStyle}>{badge}</span>
      )}
    </button>
  );
};

// ============================================================================
// STYLES
// ============================================================================

const systemBadgeStyle: React.CSSProperties = {
  fontSize: '0.65rem',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: 999,
  background: '#dbeafe',
  color: '#1e3a8a',
  whiteSpace: 'nowrap',
};

const toggleButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  color: 'var(--text-secondary)',
  cursor: 'pointer',
  padding: '0.3rem',
  borderRadius: 4,
  display: 'inline-flex',
  alignItems: 'center',
};

const sidebarItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  padding: '0.5rem 0.75rem',
  fontSize: '0.8125rem',
  border: 'none',
  cursor: 'pointer',
  textAlign: 'left',
};

const sidebarItemCollapsedStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  padding: '0.5rem 0',
  border: 'none',
  cursor: 'pointer',
};

const sidebarSectionLabelStyle: React.CSSProperties = {
  padding: '0.75rem 0.85rem 0.25rem',
  fontSize: '0.65rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.6,
  color: 'var(--text-secondary)',
};

const sidebarSectionDividerStyle: React.CSSProperties = {
  margin: '0.25rem 0.5rem',
  borderTop: '1px solid var(--border-color)',
};

const sidebarBadgeStyle: React.CSSProperties = {
  flexShrink: 0,
  fontSize: '0.65rem',
  fontWeight: 600,
  padding: '1px 6px',
  borderRadius: 999,
  background: 'var(--bg-secondary)',
  color: 'var(--text-secondary)',
  border: '1px solid var(--border-color)',
};

const loadingStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '0.75rem',
  padding: '2rem',
  flex: 1,
  height: '100%',
};

const spinnerStyle: React.CSSProperties = {
  width: '2rem',
  height: '2rem',
  border: '3px solid var(--border-color)',
  borderTopColor: '#2563eb',
  borderRadius: '50%',
  animation: 'spin 0.8s linear infinite',
};

const errorStyle: React.CSSProperties = {
  padding: '1rem 1.25rem',
  margin: '1rem',
  border: '1px solid #fecaca',
  background: '#fef2f2',
  color: '#991b1b',
  borderRadius: 6,
};
